import {useState, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {ModuleRegistry,GridApi} from 'ag-grid-community'

import { AllEnterpriseModule, IntegratedChartsModule, LicenseManager } from 'ag-grid-enterprise';
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import type { AgChartOptions } from 'ag-charts-enterprise';

import {AgGridReact} from 'ag-grid-react'
import {Box, IconButton, Typography, LinearProgress, Chip, TextField, Autocomplete, Tooltip, Button} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import CancelIcon from '@mui/icons-material/Cancel'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { ProposalDetailsModal } from '../components/BulkRating'
import axios from 'axios'
// Register the modules with proper chaining
ModuleRegistry.registerModules([
    AllEnterpriseModule,
    IntegratedChartsModule.with(AgChartsEnterpriseModule)
]);

// Set the license key
LicenseManager.setLicenseKey('_A_loophole_hidden_deep_in_the_architecture,_offering_access_to_those_daring_enough_to_find_the_keys_that_were_never_meant_to_be_discovered_(13 July 2026)__[v3]_[0102]__MTc4MzkxODgwMDAwMA==fa8ac94f2735bd2d7c124337488c29d9');

interface BulkRatingProposal {
    requestId: string
    proposalId: number
    createdAt: string
    completedAt?: string,
    originalEvent?: any,
    data: {
        meta: {
            fnsList?: Record<string, number>
            proposalsCounts?: Record<string, number>
            totalRatingTime: number
            totalDownloadTime: number
            totalDownloadAndRating: number
        }
        requestId: string
        responseS3: {
            logs: {
                key: string
                bucketName: string
            }
            quote: {
                key: string
                bucketName: string
            }
        }
        ratingStatus: {
            isSuccess: boolean
            errorMessage?: string
        }
        generatedQuote?: {
            premium: number
            totalCost: number
        }
        passThrough: {
            stateId: number
            clientId: number
            carrierId: number
            proposalId: number
            policySetId: number
            effectiveDate: string
            originalQuote?: {
                premium: number
                totalCost: number
                installmentFeesTotal: number | null
            }
            ratingTimeStamp: string
            versionKeyLocked: boolean
            versionKeyTimeStamp: string
        }
    }
}

interface BulkRatingProposalsResponse {
    requestId: string
    totalCount: number
    status: string
    createdAt: string
    completedAt: string
    proposals: BulkRatingProposal[]
    requestPayload?: {
        proposalIds?: number[] | null
        excludeIds?: number[]
        states?: string[]
        lambdaOptions?: {
            includeExecutionTree?: boolean
            includeLogs?: boolean
            includeWorksheet?: boolean
            isSecondPass?: boolean,
            includeTailEquations?: boolean
        }
    }
}

const CHUNK_SIZE = 100

/** Matches Version Manager / normalizer terminal statuses (bulk_request.status). */
function isTerminalBulkStatus(status: string | undefined): boolean {
    if (!status) return false
    const normalized = status.toUpperCase()
    return normalized === 'DONE' || normalized === 'COMPLETED' || normalized === 'CANCELLED'
}

function isBulkSuccessfullyFinished(status: string | undefined): boolean {
    if (!status) return false
    const normalized = status.toUpperCase()
    return normalized === 'DONE' || normalized === 'COMPLETED'
}

async function fetchBulkRatingProposals(requestId: string, offset?: number, limit?: number): Promise<BulkRatingProposalsResponse> {
    const body: Record<string, unknown> = {requestId}
    if (offset !== undefined && limit !== undefined) {
        body.offset = offset
        body.limit = limit
    }
    const response = await fetch('/api/bulk-rating/proposals', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body)
    })
    if (!response.ok) {
        throw new Error('Failed to fetch bulk rating proposals')
    }
    return response.json()
}

async function cancelBulkRatingRequest(requestId: string): Promise<BulkRatingProposalsResponse> {
    const response = await fetch('/api/bulk-rating/cancel', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({requestId})
    })
    if (!response.ok) {
        throw new Error('Failed to cancel bulk rating request')
    }
    return response.json()
}

function BulkRatingDetail() {
    const {requestId} = useParams<{ requestId: string }>()
    const navigate = useNavigate()
    const [proposals, setProposals] = useState<BulkRatingProposal[]>([])
    const [requestInfo, setRequestInfo] = useState<Omit<BulkRatingProposalsResponse, 'proposals'> | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [loadingProgress, setLoadingProgress] = useState<{loaded: number; total: number} | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [premiumFilter, setPremiumFilter] = useState<'all' | 'match' | 'mismatch'>('all')
    const [liveProgress, setLiveProgress] = useState<{ count: number; total: number } | null>(null)
    const [fnsFilter, setFnsFilter] = useState<string | null>(null)
    const [selectedRecord, setSelectedRecord] = useState<BulkRatingProposal | null>(null)
    const [chartData, setChartData] = useState<Array<{index: number; proposalId: number; vehicles: number; coverages: number; totalTime: number}>>([])
    const [isCancelling, setIsCancelling] = useState(false)
    const [stateValues, setStateValues] = useState<Record<string, string>>({})

    const gridApiRef = useRef<GridApi | null>(null)
    const eventSourceRef = useRef<EventSource | null>(null)
    const isCompletedRef = useRef(false)

    useEffect(() => {
        if (!requestId) return
        const id = requestId
        let cancelled = false

        async function loadProposalsInChunks() {
            setIsLoading(true)
            setError(null)
            setLoadingProgress({loaded: 0, total: 0})

            try {
                // First chunk — also gives us metadata (totalCount, status, etc.)
                const firstChunk = await fetchBulkRatingProposals(id, 0, CHUNK_SIZE)
                if (cancelled) return

                const {proposals: firstProposals, ...info} = firstChunk
                const allProposals = [...(firstProposals || [])]
                setRequestInfo(info)
                if (isTerminalBulkStatus(info.status)) {
                    setLiveProgress(null)
                }

                const total = info.totalCount ?? allProposals.length

                if (allProposals.length < total) {
                    setLoadingProgress({loaded: allProposals.length, total})

                    let offset = allProposals.length
                    while (offset < total && !cancelled) {
                        const chunk = await fetchBulkRatingProposals(id, offset, CHUNK_SIZE)
                        if (cancelled) return

                        const chunkProposals = chunk.proposals || []
                        if (chunkProposals.length === 0) break

                        allProposals.push(...chunkProposals)
                        offset += chunkProposals.length
                        setLoadingProgress({loaded: offset, total})
                    }
                }

                if (!cancelled) {
                    setProposals(allProposals)
                }
            } catch (err: any) {
                if (!cancelled) {
                    console.error('Failed to load proposals:', err)
                    setError(err.message)
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false)
                    setLoadingProgress(null)
                }
            }
        }

        loadProposalsInChunks()
        return () => { cancelled = true }
    }, [requestId])

    useEffect(() => {
        if (!requestId || !requestInfo?.totalCount) return
        const id = requestId
        if (isTerminalBulkStatus(requestInfo.status)) {
            isCompletedRef.current = true
            return
        }
        if (isCompletedRef.current) return

        // Close any existing connection before creating a new one
        if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
        }

        const eventSource = new EventSource(
            `/api/bulk-rating/progress?requestId=${id}&totalCount=${requestInfo.totalCount}`
        )
        eventSourceRef.current = eventSource

        const handleComplete = () => {
            if (isCompletedRef.current) return // Prevent duplicate handling
            isCompletedRef.current = true
            eventSource.close()
            eventSourceRef.current = null
            // Show 100% immediately before refreshing data
            setLiveProgress({count: requestInfo.totalCount, total: requestInfo.totalCount})
            // Refresh the page data in chunks
            async function refreshInChunks() {
                const firstChunk = await fetchBulkRatingProposals(id, 0, CHUNK_SIZE)
                const {proposals: firstProposals, ...info} = firstChunk
                let allProposals = firstProposals || []
                setProposals(allProposals)
                setRequestInfo(info)
                const total = info.totalCount ?? allProposals.length
                let offset = allProposals.length
                while (offset < total) {
                    const chunk = await fetchBulkRatingProposals(id, offset, CHUNK_SIZE)
                    const chunkProposals = chunk.proposals || []
                    if (chunkProposals.length === 0) break
                    allProposals = [...allProposals, ...chunkProposals]
                    offset += chunkProposals.length
                    setProposals(allProposals)
                }
                setLiveProgress(null)
            }
            refreshInChunks()
        }

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                if (data.type === 'progress') {
                    setLiveProgress({count: data.count, total: data.total})
                    // Check if count reached total - refresh data
                    if (data.count >= data.total) {
                        handleComplete()
                    }
                } else if (data.type === 'complete') {
                    handleComplete()
                }
            } catch (err) {
                console.error('SSE parse error:', err)
            }
        }

        eventSource.onerror = () => {
            // Only reconnect if we haven't completed
            if (!isCompletedRef.current) {
                console.error('SSE connection error')
            }
            eventSource.close()
            eventSourceRef.current = null
        }

        return () => {
            eventSource.close()
            eventSourceRef.current = null
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId, requestInfo?.totalCount, requestInfo?.status])

    // Update chart data when all proposals are loaded or filters change
    useEffect(() => {
        if (!isLoading && gridApiRef.current && proposals.length > 0) {
            updateChartData(gridApiRef.current)
        }
    }, [isLoading, proposals, premiumFilter, fnsFilter])

    // Fetch account meta for state name lookup
    useEffect(() => {
        axios.get(`/api/admin/account/meta`).then((response) => {
            setStateValues(response.data.stateMap as Record<string, string>)
        }).catch((error) => {
            // state names unavailable; IDs will show as-is
            console.error('Failed to fetch account meta for state lookup:', error)
        })
    }, [])

    const handleBack = () => {
        navigate('/bulk-rating')
    }

    const handleDownload = () => {
        const api = gridApiRef.current
        if (!api) return

        const exportFileName = `bulk-rating-${requestId ?? 'request'}`

        api.exportDataAsCsv({fileName: `${exportFileName}.csv`})
    }

    const handleCancel = async () => {
        if (!requestId || isCancelling) return
        const id = requestId

        const confirmed = window.confirm(
            'Are you sure you want to cancel this bulk rating request? This will stop processing any remaining proposals.'
        )
        if (!confirmed) return

        setIsCancelling(true)
        try {
            await cancelBulkRatingRequest(id)

            // Close SSE connection since request is now cancelled
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
                eventSourceRef.current = null
            }
            isCompletedRef.current = true
            setLiveProgress(null)

            // Refresh the page data in chunks to get the latest state
            const firstChunk = await fetchBulkRatingProposals(id, 0, CHUNK_SIZE)
            const {proposals: firstProposals, ...info} = firstChunk
            let allProposals = firstProposals || []
            setProposals(allProposals)
            setRequestInfo(info)
            const total = info.totalCount ?? allProposals.length
            let offset = allProposals.length
            while (offset < total) {
                const chunk = await fetchBulkRatingProposals(id, offset, CHUNK_SIZE)
                const chunkProposals = chunk.proposals || []
                if (chunkProposals.length === 0) break
                allProposals = [...allProposals, ...chunkProposals]
                offset += chunkProposals.length
                setProposals(allProposals)
            }

            // Update chart data after a short delay to allow grid to render
            setTimeout(() => {
                if (gridApiRef.current) {
                    updateChartData(gridApiRef.current)
                }
            }, 100)
        } catch (err) {
            console.error('Failed to cancel request:', err)
            setError(err instanceof Error ? err.message : 'Failed to cancel request')
        } finally {
            setIsCancelling(false)
        }
    }

    const isRequestActive = requestInfo?.status === 'PENDING' || requestInfo?.status === 'IN_PROGRESS'

    const expectedTotal = requestInfo?.totalCount ?? 0
    const isTerminal = isTerminalBulkStatus(requestInfo?.status)
    const rawCompletedCount = isTerminal
        ? (isBulkSuccessfullyFinished(requestInfo?.status)
            ? expectedTotal
            : Math.min(proposals.length, expectedTotal))
        : Math.max(liveProgress?.count ?? 0, proposals.length)
    const completedCount =
        expectedTotal > 0 ? Math.min(rawCompletedCount, expectedTotal) : rawCompletedCount
    const pendingCount = expectedTotal - completedCount
    const completedPercent = expectedTotal > 0 ? Math.round((completedCount / expectedTotal) * 100) : 0

    const successCount = proposals.filter(p => p.data?.ratingStatus?.isSuccess).length
    const failedCount = proposals.filter(p => p.data?.ratingStatus && !p.data.ratingStatus.isSuccess).length

    const premiumMatchCount = proposals.filter(p => {
        const original = p.data?.passThrough?.originalQuote?.totalCost
        const generated = p.data?.generatedQuote?.totalCost
        return original !== undefined && generated !== undefined && original === generated
    }).length
    const premiumMismatchCount = proposals.filter(p => {
        const original = p.data?.passThrough?.originalQuote?.totalCost
        const generated = p.data?.generatedQuote?.totalCost
        return original !== undefined && generated !== undefined && original !== generated
    }).length
    const premiumCompareTotal = premiumMatchCount + premiumMismatchCount
    const premiumMatchPercent = premiumCompareTotal > 0 ? ((premiumMatchCount / premiumCompareTotal) * 100).toFixed(1) : '0.0'

    const allFnsKeys = Array.from(
        new Set(
            proposals.flatMap(p => Object.keys(p.data?.meta?.fnsList ?? {}))
        )
    ).sort()

    const filteredProposals = proposals.filter(p => {
        if (premiumFilter !== 'all') {
            const original = p.data?.passThrough?.originalQuote?.totalCost
            const generated = p.data?.generatedQuote?.totalCost
            const isMatch = original !== undefined && generated !== undefined && original === generated
            if (premiumFilter === 'match' && !isMatch) return false
            if (premiumFilter === 'mismatch' && isMatch) return false
        }
        if (fnsFilter) {
            const fnsList = p.data?.meta?.fnsList
            if (!fnsList || !(fnsFilter in fnsList)) return false
        }
        return true
    })

    const updateChartData = (api: GridApi) => {
        const displayedRows: BulkRatingProposal[] = []
        api.forEachNodeAfterFilterAndSort((node) => {
            if (node.data) displayedRows.push(node.data)
        })
        setChartData(displayedRows.map((p, idx) => ({
            index: idx + 1,
            proposalId: p.proposalId,
            vehicles: p.data?.meta?.proposalsCounts?.['Custom.ISOCAVehicle'] ?? 0,
            coverages: p.data?.meta?.proposalsCounts?.['Custom.ISOCAVehCovg'] ?? 0,
            totalTime: (p.data?.meta?.totalDownloadAndRating ?? 0) / 1000
        })))
    }

    return (
        <div>
            <div style={{display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '16px'}}>
                <IconButton onClick={handleBack} size="small">
                    <ArrowBackIcon/>
                </IconButton>
                <Typography variant="h5" component="h1" style={{margin: 0}}>
                    Bulk Rating Request: {requestId}
                </Typography>
            </div>

            {error && (
                <Typography color="error" style={{marginBottom: '16px'}}>
                    {error}
                </Typography>
            )}

            {!isLoading && requestInfo && (
                <Box sx={{
                    marginBottom: '20px',
                    padding: '16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa'
                }}>
                    <LinearProgress
                        variant="determinate"
                        value={completedPercent}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            marginBottom: 2,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 5
                            }
                        }}
                    />

                    <Box sx={{display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap'}}>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                EXECUTION STATUS
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Typography variant="h6" sx={{fontWeight: 700, color: '#1976d2'}}>
                                    {completedPercent}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ({completedCount}/{expectedTotal})
                                </Typography>
                                <Chip label={`${successCount} Success`} color="success" size="small"/>
                                <Chip label={`${failedCount} Failed`} color="error" size="small"/>
                                {pendingCount > 0 && (
                                    <Chip label={`${pendingCount} Pending`} color="default" size="small"/>
                                )}
                                {requestInfo?.status === 'CANCELLED' && (
                                    <Chip label="Cancelled" color="warning" size="small"/>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                PREMIUM VALIDATION
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Typography variant="h6" sx={{fontWeight: 700, color: '#4caf50'}}>
                                    {premiumMatchPercent}%
                                </Typography>
                                <Chip
                                    label={`${premiumMatchCount} Match`}
                                    color="success"
                                    size="small"
                                    variant={premiumFilter === 'match' ? 'filled' : 'outlined'}
                                    onClick={() => setPremiumFilter(premiumFilter === 'match' ? 'all' : 'match')}
                                    sx={{cursor: 'pointer'}}
                                />
                                <Chip
                                    label={`${premiumMismatchCount} Mismatch`}
                                    color="warning"
                                    size="small"
                                    variant={premiumFilter === 'mismatch' ? 'filled' : 'outlined'}
                                    onClick={() => setPremiumFilter(premiumFilter === 'mismatch' ? 'all' : 'mismatch')}
                                    sx={{cursor: 'pointer'}}
                                />
                                {premiumFilter !== 'all' && (
                                    <Chip
                                        label="Clear Filter"
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setPremiumFilter('all')}
                                        onDelete={() => setPremiumFilter('all')}
                                        sx={{cursor: 'pointer'}}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                FUNCTION FILTER
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Autocomplete
                                    size="small"
                                    options={allFnsKeys}
                                    value={fnsFilter}
                                    onChange={(_, newValue) => setFnsFilter(newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Select function..."/>
                                    )}
                                    sx={{minWidth: 250}}
                                />
                                {fnsFilter && (
                                    <Chip
                                        label={fnsFilter}
                                        size="small"
                                        color="primary"
                                        onDelete={() => setFnsFilter(null)}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                ACTIONS
                            </Typography>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<DownloadIcon/>}
                                    onClick={handleDownload}
                                    disabled={filteredProposals.length === 0}
                                >
                                    Download Bulk Report
                                </Button>
                                {isRequestActive && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<CancelIcon/>}
                                        onClick={handleCancel}
                                        disabled={isCancelling}
                                        sx={{
                                            backgroundColor: '#d32f2f',
                                            '&:hover': { backgroundColor: '#b71c1c' }
                                        }}
                                    >
                                        {isCancelling ? 'Cancelling...' : 'Cancel Request'}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {requestInfo?.requestPayload && (
                        <Box sx={{display: 'flex', gap: 3, marginTop: 2, paddingTop: 2, borderTop: '1px solid #e0e0e0', flexWrap: 'wrap'}}>
                            {requestInfo.requestPayload.lambdaOptions && (
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                        Rating Engine Options
                                    </Typography>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        {requestInfo.requestPayload.lambdaOptions.includeExecutionTree && (
                                            <Chip label="Execution Tree" size="small" color="info" variant="outlined"/>
                                        )}
                                        {requestInfo.requestPayload.lambdaOptions.includeLogs && (
                                            <Chip label="Logs" size="small" color="info" variant="outlined"/>
                                        )}
                                        {requestInfo.requestPayload.lambdaOptions.isSecondPass && (
                                            <Chip label="Second Pass" size="small" color="info" variant="outlined"/>
                                        )}
                                        {requestInfo.requestPayload.lambdaOptions.includeWorksheet && (
                                            <Chip label="Worksheet" size="small" color="info" variant="outlined"/>
                                        )}
                                        {requestInfo.requestPayload.lambdaOptions.includeTailEquations && (
                                            <Chip label="TailEquations" size="small" color="info" variant="outlined"/>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {requestInfo.requestPayload.proposalIds && requestInfo.requestPayload.proposalIds.length > 0 ? (
                                <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                        PROPOSAL IDS ({requestInfo.requestPayload.proposalIds.length})
                                    </Typography>
                                    <Typography variant="body2" sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}>
                                        {requestInfo.requestPayload.proposalIds.length > 10
                                            ? `${requestInfo.requestPayload.proposalIds.slice(0, 10).join(', ')}...`
                                            : requestInfo.requestPayload.proposalIds.join(', ')}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {requestInfo.requestPayload.states && requestInfo.requestPayload.states.length > 0 && (
                                        <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                                STATES ({requestInfo.requestPayload.states.length})
                                            </Typography>
                                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                                                {requestInfo.requestPayload.states.map(state => (
                                                    <Chip key={state} label={state} size="small" variant="outlined"/>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                    {requestInfo.requestPayload.excludeIds && requestInfo.requestPayload.excludeIds.length > 0 && (
                                        <Box sx={{borderLeft: '1px solid #e0e0e0', paddingLeft: 3, display: 'flex', flexDirection: 'column', gap: 1}}>
                                            <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                                EXCLUDED IDS ({requestInfo.requestPayload.excludeIds.length})
                                            </Typography>
                                            <Typography variant="body2" sx={{fontFamily: 'monospace', fontSize: '0.8rem'}}>
                                                {requestInfo.requestPayload.excludeIds.length > 10
                                                    ? `${requestInfo.requestPayload.excludeIds.slice(0, 10).join(', ')}...`
                                                    : requestInfo.requestPayload.excludeIds.join(', ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {!isLoading && chartData.length > 0 && (
                <Box sx={{
                    marginBottom: '20px',
                    padding: '16px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa'
                }}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600, display: 'block', marginBottom: 2}}>
                        PERFORMANCE METRICS
                    </Typography>
                    <Box sx={{display: 'flex', gap: 2, marginBottom: 2}}>
                        {(() => {
                            const times = chartData.map(d => d.totalTime).filter(t => t > 0)
                            if (times.length === 0) return null
                            const avg = times.reduce((a, b) => a + b, 0) / times.length
                            const min = Math.min(...times)
                            const max = Math.max(...times)
                            const sorted = [...times].sort((a, b) => a - b)
                            const mid = Math.floor(sorted.length / 2)
                            const median = sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
                            return (
                                <>
                                    <Box sx={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #90caf9'
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                            AVERAGE TIME
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 700, color: '#1976d2'}}>
                                            {avg.toFixed(3)}s
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        backgroundColor: '#f3e5f5',
                                        border: '1px solid #ce93d8'
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                            MEDIAN TIME
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 700, color: '#9c27b0'}}>
                                            {median.toFixed(3)}s
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        backgroundColor: '#e8f5e9',
                                        border: '1px solid #a5d6a7'
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                            MIN TIME
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 700, color: '#4caf50'}}>
                                            {min.toFixed(3)}s
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        flex: 1,
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        backgroundColor: '#fff3e0',
                                        border: '1px solid #ffcc80'
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{fontWeight: 600}}>
                                            MAX TIME
                                        </Typography>
                                        <Typography variant="h6" sx={{fontWeight: 700, color: '#ff9800'}}>
                                            {max.toFixed(3)}s
                                        </Typography>
                                    </Box>
                                </>
                            )
                        })()}
                    </Box>
                    <Box sx={{height: 300, width: '100%', overflow: 'hidden'}}>
                        <AgCharts
                            options={{
                                data: chartData,
                                series: [
                                    {
                                        type: 'bar',
                                        xKey: 'index',
                                        yKey: 'vehicles',
                                        yName: 'Vehicles',
                                        fill: '#4caf50',
                                        yKeyAxis: 'ySecondary'
                                    },
                                    {
                                        type: 'bar',
                                        xKey: 'index',
                                        yKey: 'coverages',
                                        yName: 'Coverages',
                                        fill: '#2196f3',
                                        yKeyAxis: 'ySecondary'
                                    },
                                    {
                                        type: 'line',
                                        xKey: 'index',
                                        yKey: 'totalTime',
                                        yName: 'Total Time (s)',
                                        stroke: '#ff9800',
                                        marker: { enabled: false }
                                    }
                                ],
                                axes: {
                                    y: {
                                        type: 'number',
                                        position: 'left',
                                        title: { text: 'Time (s)' }
                                    },
                                    ySecondary: {
                                        type: 'number',
                                        position: 'right',
                                        title: { text: 'Count Vehicles / Coverages' }
                                    }
                                },
                                legend: { position: 'bottom' },
                                zoom: {
                                    enabled: true,
                                    axes: 'y'
                                }
                            } as AgChartOptions}
                        />
                    </Box>
                </Box>
            )}


            {isLoading ? (
                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: 4}}>
                    <LinearProgress
                        variant="determinate"
                        value={loadingProgress && loadingProgress.total > 0 ? Math.round((loadingProgress.loaded / loadingProgress.total) * 100) : 0}
                        sx={{width: '100%', maxWidth: 400, height: 8, borderRadius: 4}}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {loadingProgress && loadingProgress.total > 0
                            ? `Loading proposals: ${loadingProgress.loaded}/${loadingProgress.total}`
                            : 'Loading...'}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{height: 'calc(100vh - 600px)', width: '100%', minHeight: 400}}>
                    <div className="ag-theme-balham" style={{height: '100%', width: '100%'}}>
                        <AgGridReact
                            rowData={filteredProposals}
                            rowHeight={34}
                            ensureDomOrder={true}
                            autoSizeStrategy={{type: 'fitGridWidth', defaultMinWidth: 100}}
                            {...({cellSelection: true} as any)}
                            enableCharts={true}
                            enableRangeSelection={true}
                            rowGroupPanelShow="always"
                            autoGroupColumnDef={{
                                headerName: 'Group',
                                minWidth: 300,
                                cellRendererParams: {
                                    innerRenderer: (params: any) => {
                                        const node = params.node
                                        if (!node.group) return params.value

                                        const children: any[] = []
                                        node.allLeafChildren?.forEach((child: any) => {
                                            if (child.data) children.push(child.data)
                                        })

                                        const times = children
                                            .map((c: any) => c.data?.meta?.totalDownloadAndRating)
                                            .filter((v: any): v is number => typeof v === 'number')

                                        if (times.length === 0) {
                                            return <span>{node.key} ({node.allChildrenCount})</span>
                                        }

                                        const min = Math.min(...times)
                                        const max = Math.max(...times)
                                        const avg = Math.round(times.reduce((a: number, b: number) => a + b, 0) / times.length)

                                        return (
                                            <span>
                                                {node.key} ({node.allChildrenCount}) | Min: {min}ms | Max: {max}ms | Avg: {avg}ms
                                            </span>
                                        )
                                    }
                                }
                            }}
                            onGridReady={(event: any) => {
                                gridApiRef.current = event.api
                                updateChartData(event.api)
                            }}
                            onSortChanged={(event: any) => {
                                updateChartData(event.api)
                            }}
                            onFilterChanged={(event: any) => {
                                updateChartData(event.api)
                            }}
                            columnDefs={[
                                {
                                    field: 'proposalId',
                                    headerName: 'Proposal',
                                    flex: 0.8,
                                    width: 120,
                                    minWidth: 120,
                                    cellRenderer: (params: any) => {
                                        return (
                                            <span
                                                onClick={() => setSelectedRecord(params.data)}
                                                style={{
                                                    cursor: 'pointer',
                                                    color: '#1976d2',
                                                    textDecoration: 'underline'
                                                }}
                                            >
                                                {params.value}
                                            </span>
                                        )
                                    }
                                },
                                {
                                    field: 'data.passThrough.stateId',
                                    headerName: 'State',
                                    flex: 0.6,
                                    minWidth: 90,
                                    valueGetter: (params) => {
                                        const stateId = params.data?.data?.passThrough?.stateId;
                                        if (stateId === null || stateId === undefined) {
                                            return '';
                                        }
                                        const mapped = stateValues?.[stateId];
                                        return mapped !== undefined ? mapped : String(stateId);
                                    }
                                },
                                {
                                    field: 'data.passThrough.effectiveDate',
                                    headerName: 'Effective Date',
                                    flex: 1,
                                    minWidth: 130
                                },
                                {
                                    field: 'data.passThrough.originalQuote.totalCost',
                                    headerName: 'Original Total Cost',
                                    flex: 1,
                                    minWidth: 140,
                                    colSpan: (params: any) => params.data?.data?.ratingStatus?.isSuccess === false ? 8 : 1,
                                    cellClass: (params: any) => params.data?.data?.ratingStatus?.isSuccess === false ? '' : 'ag-right-aligned-cell',
                                    cellRenderer: (params: any) => {
                                        if (params.data?.data?.ratingStatus?.isSuccess === false) {
                                            const errorMessage = params.data?.data?.ratingStatus?.errorMessage ?? 'Rating failed'
                                            return (
                                                <Tooltip title={errorMessage}>
                                                    <span style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        color: '#c62828',
                                                        fontStyle: 'italic',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        ⚠️ {errorMessage}
                                                    </span>
                                                </Tooltip>
                                            )
                                        }
                                        const value = params.data?.data?.passThrough?.originalQuote?.totalCost
                                        if (value === null || value === undefined) return '-'
                                        return new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }).format(value)
                                    },
                                    cellStyle: (params: any) => {
                                        if (params.data?.data?.ratingStatus?.isSuccess === false) {
                                            return { backgroundColor: '#ffebee' }
                                        }
                                        const original = params.data?.data?.passThrough?.originalQuote?.totalCost
                                        const generated = params.data?.data?.generatedQuote?.totalCost
                                        if (original !== undefined && generated !== undefined && original !== generated) {
                                            return { backgroundColor: '#fff9c4' }
                                        }
                                        return {}
                                    }
                                },
                                {
                                    field: 'data.generatedQuote.totalCost',
                                    headerName: 'Generated Total Cost',
                                    flex: 1,
                                    minWidth: 150,
                                    cellClass: 'ag-right-aligned-cell',
                                    valueFormatter: (params: any) => {
                                        if (params.value === null || params.value === undefined) return '-'
                                        return new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        }).format(params.value)
                                    },
                                    cellStyle: (params: any) => {
                                        const original = params.data?.data?.passThrough?.originalQuote?.totalCost
                                        const generated = params.data?.data?.generatedQuote?.totalCost
                                        if (original !== undefined && generated !== undefined && original !== generated) {
                                            return { backgroundColor: '#fff9c4' }
                                        }
                                        return {}
                                    }
                                },
                                {
                                    field: 'data.meta.totalRatingTime',
                                    headerName: 'Algo',
                                    flex: 0.8,
                                    minWidth: 120,
                                    cellClass: 'ag-right-aligned-cell',
                                    valueGetter: (params: any) => params.data?.data?.meta?.totalRatingTime,
                                    valueFormatter: (params: any) => {
                                        if (params.value === null || params.value === undefined) return '-'
                                        return `${(params.value / 1000).toFixed(3)}s`
                                    }
                                },
                                {
                                    field: 'data.meta.totalDownloadTime',
                                    headerName: 'S3',
                                    flex: 0.8,
                                    minWidth: 130,
                                    cellClass: 'ag-right-aligned-cell',
                                    valueGetter: (params: any) => params.data?.data?.meta?.totalDownloadTime,
                                    valueFormatter: (params: any) => {
                                        if (params.value === null || params.value === undefined) return '-'
                                        return `${(params.value / 1000).toFixed(3)}s`
                                    }
                                },
                                {
                                    field: 'data.meta.totalDownloadAndRating',
                                    headerName: 'Total',
                                    flex: 0.8,
                                    minWidth: 120,
                                    cellClass: 'ag-right-aligned-cell',
                                    valueGetter: (params: any) => params.data?.data?.meta?.totalDownloadAndRating,
                                    valueFormatter: (params: any) => {
                                        if (params.value === null || params.value === undefined) return '-'
                                        return `${(params.value / 1000).toFixed(3)}s`
                                    }
                                },
                                {
                                    colId: 'vehicles',
                                    headerName: 'Vehicles',
                                    flex: 0.6,
                                    minWidth: 90,
                                    valueGetter: (params: any) => {
                                        const val = params.data?.data?.meta?.proposalsCounts?.['Custom.ISOCAVehicle']
                                        return typeof val === 'number' ? val : null
                                    },
                                    comparator: (a: any, b: any) => (a ?? 0) - (b ?? 0)
                                },
                                {
                                    colId: 'coverages',
                                    headerName: 'Veh Covgs',
                                    flex: 0.6,
                                    minWidth: 100,
                                    valueGetter: (params: any) => {
                                        const val = params.data?.data?.meta?.proposalsCounts?.['Custom.ISOCAVehCovg']
                                        return typeof val === 'number' ? val : null
                                    },
                                    comparator: (a: any, b: any) => (a ?? 0) - (b ?? 0)
                                },
                                {
                                    headerName: 'Functions',
                                    flex: 0.6,
                                    minWidth: 100,
                                    cellRenderer: (params: any) => {
                                        const fnsList = params.data?.data?.meta?.fnsList
                                        if (!fnsList || Object.keys(fnsList).length === 0) return '-'
                                        const entries = Object.entries(fnsList)
                                        const fullText = entries.map(([k, v]) => `${k}: ${v}`).join('\n')
                                        const count = entries.length
                                        return (
                                            <Tooltip title={<pre
                                                style={{margin: 0, whiteSpace: 'pre-wrap'}}>{fullText}</pre>} arrow>
                                                <span style={{
                                                    cursor: 'pointer',
                                                    color: '#1976d2',
                                                    fontSize: '0.9em'
                                                }}>
                                                    {count} {count === 1 ? 'function' : 'functions'}
                                                </span>
                                            </Tooltip>
                                        )
                                    }
                                }
                            ]}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                                enableRowGroup: true,

                            }}

                        />
                    </div>
                </Box>
            )}

            {selectedRecord && (
                <ProposalDetailsModal
                    open={!!selectedRecord}
                    onClose={() => setSelectedRecord(null)}
                    record={selectedRecord}
                    onNext={() => {
                        const currentIndex = filteredProposals.findIndex(p => p.proposalId === selectedRecord.proposalId)
                        if (currentIndex < filteredProposals.length - 1) {
                            setSelectedRecord(filteredProposals[currentIndex + 1])
                        }
                    }}
                    onPrevious={() => {
                        const currentIndex = filteredProposals.findIndex(p => p.proposalId === selectedRecord.proposalId)
                        if (currentIndex > 0) {
                            setSelectedRecord(filteredProposals[currentIndex - 1])
                        }
                    }}
                    hasNext={filteredProposals.findIndex(p => p.proposalId === selectedRecord.proposalId) < filteredProposals.length - 1}
                    hasPrevious={filteredProposals.findIndex(p => p.proposalId === selectedRecord.proposalId) > 0}
                />
            )}
        </div>
    )
}

export default BulkRatingDetail
